/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {Dispatch} from 'redux';
import {ToolAssetType} from '@ion/core/src/types/entities';
import {
  getDisconnectedToolReport,
  setHoveredAsset,
  setSelectedAsset,
  setTool,
  setToolDetailView
} from '../../store/actions/tracking';

/**
 * Dispatches the exact sequence that opens a tool's detail view -
 * `setToolDetailView`/`setTool`/`setHoveredAsset` - shared by
 * `ContainedAsset.tsx`'s own `showToolDetail` and the current-theme Tags
 * accordion's `onNameClick`, so both stay in sync rather than hand-copying
 * the same 3-call sequence.
 *
 * ```typescript
 *
 *   import {openToolDetailPanel} from
 *     '@/routes/tracking/toolDetailActions';
 *   ...
 *
 *   openToolDetailPanel(dispatch, tool);
 *
 * ```
 *
 * @param dispatch the redux dispatch function
 * @param tool     the tool whose detail view is opening
 */
export const openToolDetailPanel = (dispatch: Dispatch, tool: ToolAssetType) => {
  dispatch(setToolDetailView(true));
  dispatch(setTool(tool) as any);
  dispatch(setHoveredAsset(null, null) as any);
};

/**
 * The Tags accordion's simple "click a tag" flow: fetch the tool's last-
 * known report when it has none yet, select the tool's parent asset, then
 * open the tool's detail view via `openToolDetailPanel`. `ContainedAsset.tsx`'s
 * own `selectTool` additionally has a busy-state UI and a "skip the fetch for
 * the tool already showing" optimization neither this helper nor the Tags
 * accordion need, so it keeps its own fetch/select sequence and only reuses
 * `openToolDetailPanel` for the shared 3-call core.
 *
 * ```typescript
 *
 *   import {openToolDetail} from
 *     '@/routes/tracking/toolDetailActions';
 *   ...
 *
 *   openToolDetail(dispatch, tool, asset);
 *
 * ```
 *
 * @param dispatch the redux dispatch function
 * @param tool     the tool whose detail view is opening
 * @param asset    the tool's parent asset, to select alongside it
 */
export const openToolDetail = (dispatch: Dispatch, tool: ToolAssetType, asset: unknown) => {
  if (!tool.tagReport) {
    dispatch(getDisconnectedToolReport(tool) as any);
  }
  dispatch(setSelectedAsset({asset}) as any);
  openToolDetailPanel(dispatch, tool);
};
